import { useState, useEffect, useCallback } from 'react';
import type { Engin, TypeInventaire, Inventaire, SignaturesResponse } from './types';
import * as api from './api';

declare global {
  interface Window {
    ouvrirResumeEngin?: (btn: { getAttribute: (name: string) => string | null }) => void;
    ouvrirDetailInventaire?: (id: number, opts?: { preserveScroll?: boolean }) => void;
    ouvrirDetailSansInventaire?: (btn: { getAttribute: (name: string) => string | null }) => void;
    ouvrirModalSignerInventaire?: (btn: { getAttribute: (name: string) => string | null }) => void;
    ouvrirModalSignerInventaireGlobale?: () => void;
    inventairesCanSignEngin?: boolean;
    inventairesCanSignerInventaireGlobale?: boolean;
  }
}

function dateDuJour(): string {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/** Date max sélectionnable : inventaire 7h–7h, donc avant 6h30 on ne peut pas choisir le jour courant (aligné inventaires.js). */
function getDateEditable(): string {
  const d = new Date();
  const h = d.getHours();
  const m = d.getMinutes();
  if (h > 6 || (h === 6 && m >= 30)) {
    return dateDuJour();
  }
  const hier = new Date(d);
  hier.setDate(hier.getDate() - 1);
  return hier.getFullYear() + '-' + String(hier.getMonth() + 1).padStart(2, '0') + '-' + String(hier.getDate()).padStart(2, '0');
}

function escapeHtml(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fakeBtn(attrs: Record<string, string>): { getAttribute: (name: string) => string | null } {
  return {
    getAttribute(name: string) {
      return attrs[name] ?? null;
    },
  };
}

export default function App() {
  const [date, setDate] = useState(dateDuJour);
  const [engins, setEngins] = useState<Engin[]>([]);
  const [types, setTypes] = useState<TypeInventaire[]>([]);
  const [inventaires, setInventaires] = useState<Inventaire[]>([]);
  const [signatures, setSignatures] = useState<SignaturesResponse>({ engin_ids: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    const dateToFetch = date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : dateDuJour();
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [enginsRes, typesRes, invRes, sigRes] = await Promise.all([
        api.fetchEngins(),
        api.fetchTypes(),
        api.fetchInventaires(dateToFetch),
        api.fetchSignatures(dateToFetch),
      ]);
      setEngins(Array.isArray(enginsRes) ? (enginsRes as Engin[]) : []);
      setTypes(Array.isArray(typesRes) ? (typesRes as TypeInventaire[]) : []);
      setInventaires(Array.isArray(invRes) ? (invRes as Inventaire[]) : []);
      setSignatures(sigRes?.engin_ids ? sigRes : { engin_ids: [] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (window as unknown as { inventairesReactRefresh?: () => void }).inventairesReactRefresh = () => load(true);
    return () => {
      delete (window as unknown as { inventairesReactRefresh?: () => void }).inventairesReactRefresh;
    };
  }, [load]);

  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ type?: string; entite_id?: string; inventaire_id?: number; id?: number }>;
      const data = ev.detail;
      if (data?.type !== 'INVENTAIRE_UPDATE') return;
      const myEntiteId = (window as unknown as { currentUser?: { entite_physique_id?: string } }).currentUser?.entite_physique_id;
      if (myEntiteId != null && String(data.entite_id) !== String(myEntiteId)) return;
      const invId = data.inventaire_id ?? data.id;
      const modal = document.getElementById('modalDetailInventaire');
      const idEl = document.getElementById('modalDetailInventaireId');
      const openInvId = idEl && (idEl as HTMLInputElement).value ? parseInt((idEl as HTMLInputElement).value, 10) : null;
      const modalOuvertPourCetInv =
        modal &&
        !modal.classList.contains('hidden') &&
        invId != null &&
        Number(invId) === openInvId;
      load(modalOuvertPourCetInv);
    };
    document.addEventListener('sse:data', handler);
    return () => document.removeEventListener('sse:data', handler);
  }, [load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    const onFocus = () => { load(); };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) load();
    };
    const onSseOpen = () => { load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('sse:open', onSseOpen);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('sse:open', onSseOpen);
    };
  }, [load]);

  const mapInv: Record<string, Inventaire[]> = {};
  inventaires.forEach((inv) => {
    const key = `${inv.engin_id}-${inv.type_inventaire_id}`;
    if (!mapInv[key]) mapInv[key] = [];
    mapInv[key].push(inv);
  });

  const signedIds = (signatures.engin_ids ?? []).map((id) => String(id));
  const details = signatures.details ?? {};
  const canSignEngin = typeof window.inventairesCanSignEngin !== 'undefined' && window.inventairesCanSignEngin;
  /** Plafond pour la saisie courante (7h–7h) : on ne peut pas choisir une date « future » d’inventaire. Les dates passées restent sélectionnables pour consulter l’historique (comme inventaires.js : seul max sur l’input). */
  const maxDate = getDateEditable();

  const isCompleteDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');

  useEffect(() => {
    if (!date || !isCompleteDate(date)) return;
    if (maxDate && date > maxDate) {
      setDate(maxDate);
    }
  }, [maxDate, date]);

  const canSignerInventaireGlobale =
    typeof window.inventairesCanSignerInventaireGlobale !== 'undefined' && window.inventairesCanSignerInventaireGlobale;
  const showSignerInventaireGlobale = canSignerInventaireGlobale && engins.length > 0 && types.length > 0;

  const onDateInputChange = (v: string) => {
    if (!v) {
      setDate('');
      return;
    }
    if (!isCompleteDate(v)) {
      setDate(v);
      return;
    }
    let next = v;
    if (maxDate && v > maxDate) next = maxDate;
    setDate(next);
  };

  return (
    <>
      <div className="mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-slate-200 shrink-0 min-w-0">
        <div className="flex flex-row items-end justify-between gap-3 sm:gap-4 min-w-0">
          <div className="inventaires-date-wrapper flex-1 min-w-0 w-full max-w-full sm:max-w-none">
            <label className="block text-xs font-black text-slate-400 uppercase mb-1 sm:mb-2">Date d&apos;inventaire</label>
            <input
              type="date"
              id="dateInventaire"
              value={date}
              max={maxDate}
              onChange={(e) => onDateInputChange(e.target.value)}
              title={
                'Dates passées : consultation. Saisie limitée au plus tard au jour d’inventaire en cours (7h–7h, plafond affiché).'
              }
              className="date-icon-dark w-full max-w-full min-w-0 sm:w-auto box-border px-3 sm:px-4 py-2.5 sm:py-2 border border-slate-300 rounded-lg text-sm font-bold focus:ring-2 focus:ring-lime-500 focus:border-lime-500 bg-white text-slate-800 touch-manipulation"
            />
          </div>
          <div className="flex gap-2 justify-end shrink-0">
            {showSignerInventaireGlobale && typeof window.ouvrirModalSignerInventaireGlobale === 'function' && (
              <button
                type="button"
                onClick={() => window.ouvrirModalSignerInventaireGlobale?.()}
                className="min-h-[44px] px-4 sm:px-5 py-2.5 sm:py-2 bg-purple-600 text-white rounded-lg font-black uppercase text-xs hover:bg-purple-700 transition-colors shadow-lg touch-manipulation"
              >
                Signer l&apos;inventaire
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar min-h-0 -mx-1 px-1">
        {error && (
          <div className="text-center text-amber-600 py-12">
            <p>{error}</p>
          </div>
        )}
        {loading && !inventaires.length && !error && (
          <div className="text-center text-slate-400 py-12">
            <p>Chargement des inventaires...</p>
          </div>
        )}
        {!loading && !error && engins.length === 0 && (
          <div className="text-center text-amber-600 py-12">
            <p>Vous devez être affecté à une entité pour accéder aux inventaires.</p>
          </div>
        )}
        {!loading && !error && engins.length > 0 && types.length === 0 && (
          <div className="text-center text-amber-600 py-12">
            <p>Aucun type d&apos;inventaire configuré. Configurez les types dans le Magasin Incendie.</p>
          </div>
        )}
        {!loading && !error && engins.length > 0 && types.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {engins.map((engin) => {
              const enginNumOps = (engin.numero_ops != null ? String(engin.numero_ops).trim() : '') || '';
              const typesPourEngin = types.filter((t) => {
                if (t.actif === false) return false;
                if (t.type_engin !== engin.type_engin) return false;
                const typeNumOps = (t.numero_ops != null ? String(t.numero_ops).trim() : '') || '';
                return typeNumOps === enginNumOps || (typeNumOps === '' && !t.engin_template_id);
              });
              if (typesPourEngin.length === 0) return null;
              const allTermine = typesPourEngin.every((t) => {
                const key = `${engin.id}-${t.id}`;
                const invs = mapInv[key];
                const inv = invs?.[0];
                return inv && (inv.statut || '').toUpperCase() === 'TERMINE';
              });
              const alreadySigned = signedIds.includes(String(engin.id));
              const showSignBtn = canSignEngin && allTermine && !alreadySigned;
              const carro = (engin.num_carrosserie || '').toString().trim() || '—';
              const immat = (engin.immatriculation || '').toString().trim() || '—';
              const sig = details[String(engin.id)];
              const signataireLabel = sig?.signataire_nom ?? '—';
              const resumeBtn = fakeBtn({ 'data-engin-id': engin.id, 'data-engin-nom': engin.nom || '' });

              return (
                <div key={engin.id} className="border border-slate-200 rounded-lg p-3 sm:p-4 bg-slate-50 flex flex-col">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <h3 className="text-sm font-black text-slate-800 uppercase">{escapeHtml(engin.nom)}</h3>
                    <div className="flex gap-1 flex-wrap">
                      {showSignBtn && typeof window.ouvrirModalSignerInventaire === 'function' && (
                        <button
                          type="button"
                          onClick={() => window.ouvrirModalSignerInventaire?.(resumeBtn)}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 touch-manipulation"
                        >
                          Signer inventaire
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => window.ouvrirResumeEngin?.(resumeBtn)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-600 text-white hover:bg-slate-700 touch-manipulation"
                      >
                        Résumé
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mb-3">
                    Carrosserie: {escapeHtml(carro)} — Immatriculation: {escapeHtml(immat)}
                  </div>
                  {alreadySigned && (
                    <p className="text-xs font-bold text-lime-700 mb-2">Inventaire signé par {escapeHtml(signataireLabel)}</p>
                  )}
                  <div className="flex flex-col gap-2 flex-1 min-h-0">
                    {typesPourEngin.map((typeInv) => {
                      const key = `${engin.id}-${typeInv.id}`;
                      const invs = mapInv[key];
                      const inv = invs?.[0];
                      const label = typeInv.nom;
                      let btnStyle: string;
                      if (inv) {
                        const statutInv = (inv.statut || '').toUpperCase();
                        const nbAbsences = parseInt(String(inv.nb_absences), 10) || 0;
                        const nbAnomalies = parseInt(String(inv.nb_anomalies), 10) || 0;
                        if (statutInv === 'TERMINE' && nbAbsences === 0 && nbAnomalies === 0) {
                          btnStyle = 'bg-[#4caf50] text-white border-2 border-[#43a047]';
                        } else if (statutInv === 'TERMINE' && (nbAbsences > 0 || nbAnomalies > 0)) {
                          btnStyle = 'bg-[#FFA900] text-white border-2 border-[#e69800]';
                        } else {
                          btnStyle = 'bg-[#f44336] text-white border-2 border-[#e53935]';
                        }
                      } else {
                        btnStyle = 'bg-[#f44336] text-white border-2 border-[#e53935]';
                      }
                      const sansInvBtn = fakeBtn({
                        'data-engin-id': engin.id,
                        'data-type-id': typeInv.id,
                        'data-type-nom': typeInv.nom || '',
                        'data-engin-nom': engin.nom || '',
                      });

                      if (inv) {
                        return (
                          <button
                            key={typeInv.id}
                            type="button"
                            onClick={() => window.ouvrirDetailInventaire?.(inv.id)}
                            className={`px-3 py-2 rounded-lg text-sm font-bold hover:opacity-90 ${btnStyle}`}
                          >
                            {escapeHtml(label)}
                          </button>
                        );
                      }
                      return (
                        <button
                          key={typeInv.id}
                          type="button"
                          onClick={() => window.ouvrirDetailSansInventaire?.(sansInvBtn)}
                          className={`px-3 py-2 rounded-lg text-sm font-bold hover:opacity-90 ${btnStyle}`}
                        >
                          {escapeHtml(label)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
